import { useCallback } from 'react';
import { StarIcon } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useSetDefaultChecklist } from '@area-restrita/api-client/endpoints/hang-tag/hang-tag.gen';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

interface IProps {
  checklistId: number;
  checklistName: string;
  isDefault: boolean;
  disabled: boolean;
}

const SetDefaultChecklistAction: React.FC<IProps> = ({
  checklistId,
  checklistName,
  isDefault,
  disabled,
}) => {
  const queryClient = useQueryClient();
  const setDefaultChecklist = useSetDefaultChecklist({
    mutation: {
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ['/api/v1/hang-tag/check-list'],
        });
        toast.success('Checklist padrão atualizado com sucesso');
      },
      onError: () => toast.error('Erro ao atualizar o checklist padrão'),
    },
  });

  const handleConfirm = useCallback(
    () => setDefaultChecklist.mutate({ id: checklistId }),
    [checklistId, setDefaultChecklist],
  );

  if (isDefault) {
    return (
      <Button
        variant="secondary"
        size="icon-sm"
        disabled
        aria-label="Checklist padrão"
        title="Checklist padrão"
      >
        <StarIcon className="fill-current" />
      </Button>
    );
  }

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          disabled={disabled}
          aria-label={`Marcar ${checklistName} como padrão`}
          title="Marcar como padrão"
        >
          <StarIcon />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Definir checklist padrão?</AlertDialogTitle>
          <AlertDialogDescription>
            {checklistName} será usado nas execuções de rotas sem checklist
            específico. Se já houver um padrão, ele será substituído.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={setDefaultChecklist.isPending}
          >
            Confirmar
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default SetDefaultChecklistAction;
